import { Button, Card, Divider, Input, InputNumber, Modal, Select, Space, TableProps, Tag } from "antd"
import { DeleteOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons"
import { useCallback, useEffect, useState } from "react"
import { useLoading } from "../../context/LoadingContext"
import {
    apiGetListLimitCheckErrCodePackage,
    apiCreateLimitCheckErrCodePackage,
    apiDeleteLimitCheckErrCodePackage,
    apiUpdateWhitelistPackagesNeedCheck,
    apiGetListPartner,
} from "../../api"
import { toastError, toastSuccess } from "../../utils/toast"
import CustomTable from "../../components/table"

interface LimitCheckErrCodePackageItem {
    key: string
    provider: string
    cpCode: string
    dataPack: string
    errorCode: string
    limit: number
}

const PROVIDER_OPTIONS = [
    { label: "VDS", value: "vds" },
    { label: "Data Sponsor", value: "data_sponsor" },
    { label: "DS Postpaid", value: "ds_postpaid" },
]

export default function LimitCheckErrCodePackage() {
    const [data, setData] = useState<{ items: LimitCheckErrCodePackageItem[]; total: number }>({
        items: [],
        total: 0,
    })
    const [customers, setCustomers] = useState<{ label: string; value: string }[]>([])
    const [modalCreate, setModalCreate] = useState(false)
    const [modalWhitelist, setModalWhitelist] = useState(false)
    const [whitelistPackages, setWhitelistPackages] = useState<string[]>([])
    const [formData, setFormData] = useState({
        provider: "",
        cpCode: "",
        dataPack: "",
        errorCode: "",
        limit: 0,
        otpConfirm: "",
    })
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; data: LimitCheckErrCodePackageItem | null }>({
        isOpen: false,
        data: null,
    })
    const [otpConfirm, setOtpConfirm] = useState("")
    const { setIsLoading } = useLoading()

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true)
            const resp = await apiGetListLimitCheckErrCodePackage()
            if (resp.status === 200 && resp.data) {
                setData({
                    items: resp.data.items || [],
                    total: resp.data.total || 0,
                })
                // Get whitelist from response
                if (resp.data.whitelistedPackages && resp.data.whitelistedPackages.length > 0) {
                    setWhitelistPackages(resp.data.whitelistedPackages)
                }
            }
        } catch (error) {
            console.log(error)
            toastError("Có lỗi xảy ra khi tải dữ liệu")
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading])

    const fetchListPartner = useCallback(async () => {
        try {
            const resp = await apiGetListPartner()
            if (resp.status === 200 && resp.data) {
                setCustomers(
                    (resp.data.partners || []).map((item: any) => ({
                        label: item.cpCode,
                        value: item.cpCode,
                    }))
                )
            }
        } catch (error) {
            console.log(error)
        }
    }, [])

    useEffect(() => {
        fetchData()
        fetchListPartner()
    }, [fetchData, fetchListPartner])

    const handleCreate = async () => {
        if (!formData.provider || !formData.cpCode || !formData.dataPack || !formData.errorCode || formData.limit <= 0) {
            toastError("Vui lòng điền đầy đủ thông tin và limit phải lớn hơn 0")
            return
        }
        if (!formData.otpConfirm) {
            toastError("Vui lòng nhập mã OTP xác nhận")
            return
        }

        try {
            setIsLoading(true)
            const resp = await apiCreateLimitCheckErrCodePackage({
                provider: formData.provider,
                cpCode: formData.cpCode,
                dataPack: formData.dataPack,
                errorCode: formData.errorCode,
                limit: formData.limit,
                otpConfirm: formData.otpConfirm,
            })
            if (resp.status === 200) {
                toastSuccess("Tạo thành công")
                setModalCreate(false)
                setFormData({
                    provider: "",
                    cpCode: "",
                    dataPack: "",
                    errorCode: "",
                    limit: 0,
                    otpConfirm: "",
                })
                fetchData()
            }
        } catch (error: any) {
            console.log(error)
            toastError(error?.response?.data?.message || "Tạo thất bại")
        } finally {
            setIsLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteModal.data || !otpConfirm) {
            toastError("Vui lòng nhập mã OTP xác nhận")
            return
        }

        try {
            setIsLoading(true)
            const resp = await apiDeleteLimitCheckErrCodePackage({
                provider: deleteModal.data.provider,
                cpCode: deleteModal.data.cpCode,
                dataPack: deleteModal.data.dataPack,
                errorCode: deleteModal.data.errorCode,
            })
            if (resp.status === 200) {
                toastSuccess("Xóa thành công")
                setDeleteModal({ isOpen: false, data: null })
                setOtpConfirm("")
                fetchData()
            }
        } catch (error: any) {
            console.log(error)
            toastError(error?.response?.data?.message || "Xóa thất bại")
        } finally {
            setIsLoading(false)
        }
    }

    const handleUpdateWhitelist = async () => {
        if (!otpConfirm) {
            toastError("Vui lòng nhập mã OTP xác nhận")
            return
        }

        try {
            setIsLoading(true)
            const resp = await apiUpdateWhitelistPackagesNeedCheck({
                packages: whitelistPackages,
                otpConfirm: otpConfirm,
            })
            if (resp.status === 200) {
                toastSuccess("Cập nhật whitelist packages thành công")
                setModalWhitelist(false)
                setOtpConfirm("")
                fetchData()
            }
        } catch (error: any) {
            console.log(error)
            toastError(error?.response?.data?.message || "Cập nhật thất bại")
        } finally {
            setIsLoading(false)
        }
    }

    const handleAddWhitelistPackage = () => {
        setWhitelistPackages([...whitelistPackages, ""])
    }

    const handleRemoveWhitelistPackage = (index: number) => {
        setWhitelistPackages(whitelistPackages.filter((_, i) => i !== index))
    }

    const handleUpdateWhitelistPackage = (index: number, value: string) => {
        const newPackages = [...whitelistPackages]
        newPackages[index] = value
        setWhitelistPackages(newPackages)
    }

    const columns: TableProps<LimitCheckErrCodePackageItem>["columns"] = [
        {
            title: "Provider",
            dataIndex: "provider",
            key: "provider",
            render: (provider: string) => <Tag color="blue">{provider}</Tag>,
        },
        {
            title: "CpCode",
            dataIndex: "cpCode",
            key: "cpCode",
        },
        {
            title: "DataPack",
            dataIndex: "dataPack",
            key: "dataPack",
        },
        {
            title: "ErrorCode",
            dataIndex: "errorCode",
            key: "errorCode",
            render: (errorCode: string) => <Tag color="red">{errorCode}</Tag>,
        },
        {
            title: "Limit",
            dataIndex: "limit",
            key: "limit",
            align: "right",
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            render: (_, record: LimitCheckErrCodePackageItem) => (
                <Button
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => setDeleteModal({ isOpen: true, data: record })}
                >
                    Xóa
                </Button>
            ),
        },
    ]

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20, alignItems: "center" }}>
                <h2>Quản lý giới hạn kiểm tra lỗi</h2>
                <Space>
                    <Button
                        type="primary"
                        icon={<SettingOutlined />}
                        onClick={() => setModalWhitelist(true)}
                    >
                        Cấu hình Whitelist Packages
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalCreate(true)}>
                        Thêm mới
                    </Button>
                </Space>
            </div>

            <Card>
                {whitelistPackages.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <strong>Whitelist Packages: </strong>
                        {whitelistPackages.map((pkg, index) => (
                            <Tag key={index} color="green" style={{ marginRight: 8, marginBottom: 8 }}>
                                {pkg}
                            </Tag>
                        ))}
                    </div>
                )}
                <CustomTable
                    namePage="limit check error code package"
                    columns={columns}
                    dataSource={data.items}
                    total={data.total}
                />
            </Card>

            {/* Modal Create */}
            <Modal
                title="Thêm Limit Check Error Code Package"
                open={modalCreate}
                onCancel={() => {
                    setModalCreate(false)
                    setFormData({
                        provider: "",
                        cpCode: "",
                        dataPack: "",
                        errorCode: "",
                        limit: 0,
                        otpConfirm: "",
                    })
                }}
                footer={[
                    <Button key="cancel" onClick={() => setModalCreate(false)}>
                        Hủy
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleCreate}>
                        Tạo
                    </Button>,
                ]}
            >
                <Divider />
                <div style={{ marginBottom: 16 }}>
                    <p>
                        <span style={{ color: "red" }}>*</span> Provider
                    </p>
                    <Select
                        style={{ width: "100%" }}
                        placeholder="Chọn provider"
                        value={formData.provider}
                        onChange={(value) => setFormData({ ...formData, provider: value })}
                        options={PROVIDER_OPTIONS}
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <p>
                        <span style={{ color: "red" }}>*</span> CpCode
                    </p>
                    <Select
                        style={{ width: "100%" }}
                        placeholder="Chọn cpCode"
                        value={formData.cpCode}
                        onChange={(value) => setFormData({ ...formData, cpCode: value })}
                        options={customers}
                        showSearch
                        filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <p>
                        <span style={{ color: "red" }}>*</span> DataPack
                    </p>
                    <Input
                        value={formData.dataPack}
                        onChange={(e) => setFormData({ ...formData, dataPack: e.target.value })}
                        placeholder="Nhập DataPack"
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <p>
                        <span style={{ color: "red" }}>*</span> ErrorCode
                    </p>
                    <Input
                        value={formData.errorCode}
                        onChange={(e) => setFormData({ ...formData, errorCode: e.target.value })}
                        placeholder="Nhập ErrorCode"
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <p>
                        <span style={{ color: "red" }}>*</span> Limit
                    </p>
                    <InputNumber
                        style={{ width: "100%" }}
                        min={1}
                        value={formData.limit}
                        onChange={(value) => setFormData({ ...formData, limit: Number(value) || 0 })}
                        placeholder="Nhập Limit"
                    />
                </div>
                <Divider />
                <div style={{ marginTop: 16 }}>
                    <p>
                        <span style={{ color: "red" }}>*</span> Nhập mã OTP xác nhận
                    </p>
                    <Input
                        value={formData.otpConfirm}
                        onChange={(e) => setFormData({ ...formData, otpConfirm: e.target.value })}
                        placeholder="Nhập mã OTP"
                    />
                </div>
                <Divider />
            </Modal>

            {/* Modal Delete */}
            <Modal
                title="Xóa Limit Check Error Code Package"
                open={deleteModal.isOpen}
                onCancel={() => {
                    setDeleteModal({ isOpen: false, data: null })
                    setOtpConfirm("")
                }}
                footer={[
                    <Button key="cancel" onClick={() => setDeleteModal({ isOpen: false, data: null })}>
                        Hủy
                    </Button>,
                    <Button key="delete" type="primary" danger onClick={handleDelete}>
                        Xóa
                    </Button>,
                ]}
            >
                <Divider />
                {deleteModal.data && (
                    <div>
                        <p>
                            <strong>Provider:</strong> {deleteModal.data.provider}
                        </p>
                        <p>
                            <strong>CpCode:</strong> {deleteModal.data.cpCode}
                        </p>
                        <p>
                            <strong>DataPack:</strong> {deleteModal.data.dataPack}
                        </p>
                        <p>
                            <strong>ErrorCode:</strong> {deleteModal.data.errorCode}
                        </p>
                        <p>
                            <strong>Limit:</strong> {deleteModal.data.limit}
                        </p>
                    </div>
                )}
                <Divider />
                <div style={{ marginTop: 16 }}>
                    <p>
                        <span style={{ color: "red" }}>*</span> Nhập mã OTP xác nhận
                    </p>
                    <Input
                        value={otpConfirm}
                        onChange={(e) => setOtpConfirm(e.target.value)}
                        placeholder="Nhập mã OTP"
                    />
                </div>
                <Divider />
            </Modal>

            {/* Modal Whitelist */}
            <Modal
                title="Cấu hình Whitelist Packages"
                open={modalWhitelist}
                onCancel={() => {
                    setModalWhitelist(false)
                    setOtpConfirm("")
                    // Reset whitelist - will be populated from response on next fetch
                }}
                footer={[
                    <Button key="cancel" onClick={() => setModalWhitelist(false)}>
                        Hủy
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleUpdateWhitelist}>
                        Cập nhật
                    </Button>,
                ]}
                width={600}
            >
                <Divider />
                <div style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <p>
                            <strong>Whitelist Packages</strong>
                        </p>
                        <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddWhitelistPackage}>
                            Thêm Package
                        </Button>
                    </div>
                    {whitelistPackages.map((pkg, index) => (
                        <div key={index} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                            <Input
                                value={pkg}
                                onChange={(e) => handleUpdateWhitelistPackage(index, e.target.value)}
                                placeholder="Nhập tên package"
                                style={{ flex: 1 }}
                            />
                            <Button
                                type="primary"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleRemoveWhitelistPackage(index)}
                            >
                                Xóa
                            </Button>
                        </div>
                    ))}
                </div>
                <Divider />
                <div style={{ marginTop: 16 }}>
                    <p>
                        <span style={{ color: "red" }}>*</span> Nhập mã OTP xác nhận
                    </p>
                    <Input
                        value={otpConfirm}
                        onChange={(e) => setOtpConfirm(e.target.value)}
                        placeholder="Nhập mã OTP"
                    />
                </div>
                <Divider />
            </Modal>
        </div>
    )
}

